// W-0821 — Seção de Filtros: GE, praça, empreendimento, cliente, grupo de GEs, períodos, perfis, modo, ch11.
// Lookups via transfCobrancaLookups (GET /transf-cobranca/lookups).
// Cleanup pattern com flag `ativo` em useEffect (LICOES W-0510 L3).

import React, { useEffect, useState } from 'react';
import { Card, Form, Row, Col, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { transfCobrancaLookups } from '~/Services/Service';
import { filtrosSchema } from '../validationSchemas';
import { extrairMensagemErro } from '../helpers';

const PERFIS_FALLBACK = [
  { value: 2, label: 'Morador' },
  { value: 1, label: 'Construtor' },
  { value: 3, label: 'Investidor' },
  { value: 4, label: 'Exterior' },
];

const toOptions = (lista, valueKey, labelKey) =>
  (lista || []).map((item) => ({ value: item[valueKey], label: item[labelKey] }));

const SecaoFiltros = ({ valoresIniciais, onPesquisar, onLimpar, loading }) => {
  const [lookups, setLookups] = useState({
    gruposEmpresa: [],
    gruposGes: [],
    pracas: [],
    empreendimentos: [],
    perfis: [],
  });
  const [loadingLookups, setLoadingLookups] = useState(true);

  useEffect(() => {
    let ativo = true;
    setLoadingLookups(true);
    transfCobrancaLookups()
      .then((res) => {
        if (!ativo) return;
        // Backend SnakeCaseStrategy: grupos_empresa, grupos_ges, pracas, empreendimentos, perfis
        setLookups({
          gruposEmpresa: toOptions(res.grupos_empresa, 'codigo', 'apelido'),
          gruposGes: toOptions(res.grupos_ges, 'codigo', 'nome'),
          pracas: toOptions(res.pracas, 'codigo', 'descricao'),
          empreendimentos: toOptions(res.empreendimentos, 'codigo', 'nome'),
          perfis:
            res.perfis && res.perfis.length
              ? toOptions(res.perfis, 'codigo', 'descricao')
              : PERFIS_FALLBACK,
        });
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      })
      .finally(() => {
        if (!ativo) return;
        setLoadingLookups(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  return (
    <Card className="mb-3">
      <Card.Header as="h6" className="bg-light">
        Filtros da pesquisa
      </Card.Header>
      <Card.Body>
        <Formik
          enableReinitialize
          initialValues={valoresIniciais}
          validationSchema={filtrosSchema}
          onSubmit={onPesquisar}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
            resetForm,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Row>
                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Grupo Empresa</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isLoading={loadingLookups}
                      isClearable
                      placeholder="Selecione..."
                      options={lookups.gruposEmpresa}
                      value={
                        lookups.gruposEmpresa.find(
                          (o) => o.value === values.codigoGrupoEmpresa
                        ) || null
                      }
                      onChange={(opt) => {
                        setFieldValue('codigoGrupoEmpresa', opt ? opt.value : null);
                        if (opt) setFieldValue('codigoGrupoGes', null);
                        // Troca de GE limpa praça/empreendimento (filtros dependentes)
                        setFieldValue('codigoCidadeCobranca', null);
                        setFieldValue('codigoFamilia', null);
                      }}
                    />
                    {touched.codigoGrupoEmpresa && errors.codigoGrupoEmpresa && (
                      <Form.Text className="text-danger">
                        {errors.codigoGrupoEmpresa}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Grupo de GEs</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isLoading={loadingLookups}
                      isClearable
                      placeholder="Selecione..."
                      isDisabled={values.codigoGrupoEmpresa != null}
                      options={lookups.gruposGes}
                      value={
                        lookups.gruposGes.find(
                          (o) => o.value === values.codigoGrupoGes
                        ) || null
                      }
                      onChange={(opt) =>
                        setFieldValue('codigoGrupoGes', opt ? opt.value : null)
                      }
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Praça (Cidade)</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isLoading={loadingLookups}
                      isClearable
                      placeholder="Todas..."
                      options={lookups.pracas}
                      value={
                        lookups.pracas.find(
                          (o) => o.value === values.codigoCidadeCobranca
                        ) || null
                      }
                      onChange={(opt) =>
                        setFieldValue(
                          'codigoCidadeCobranca',
                          opt ? opt.value : null
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Empreendimento</Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isLoading={loadingLookups}
                      isClearable
                      placeholder="Todos..."
                      options={lookups.empreendimentos}
                      value={
                        lookups.empreendimentos.find(
                          (o) => o.value === values.codigoFamilia
                        ) || null
                      }
                      onChange={(opt) =>
                        setFieldValue('codigoFamilia', opt ? opt.value : null)
                      }
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Código do Cliente</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Opcional"
                      value={values.codigoConta == null ? '' : values.codigoConta}
                      onChange={(e) =>
                        setFieldValue(
                          'codigoConta',
                          e.target.value === '' ? null : Number(e.target.value)
                        )
                      }
                    />
                  </Form.Group>
                </Col>

                <Col xs={6} md={3} lg={2}>
                  <Form.Group>
                    <Form.Label className="small mb-1">
                      Dias mín. vencidos
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="diasMinimoVencidos"
                      value={values.diasMinimoVencidos}
                      onChange={handleChange}
                      isInvalid={
                        touched.diasMinimoVencidos && !!errors.diasMinimoVencidos
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.diasMinimoVencidos}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={6} md={3} lg={2}>
                  <Form.Group>
                    <Form.Label className="small mb-1">
                      Dias máx. vencidos
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="diasMaximoVencidos"
                      value={values.diasMaximoVencidos}
                      onChange={handleChange}
                      isInvalid={
                        touched.diasMaximoVencidos && !!errors.diasMaximoVencidos
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.diasMaximoVencidos}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">
                      Perfis do comprador
                    </Form.Label>
                    <Select
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      isLoading={loadingLookups}
                      isMulti
                      placeholder="Selecione um ou mais..."
                      options={lookups.perfis}
                      value={lookups.perfis.filter((o) =>
                        values.codigosPerfilComprador.includes(o.value)
                      )}
                      onChange={(opts) =>
                        setFieldValue(
                          'codigosPerfilComprador',
                          opts ? opts.map((o) => o.value) : []
                        )
                      }
                    />
                    {touched.codigosPerfilComprador &&
                      errors.codigosPerfilComprador && (
                        <Form.Text className="text-danger">
                          {errors.codigosPerfilComprador}
                        </Form.Text>
                      )}
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} lg={4} className="d-flex align-items-end">
                  <Form.Group className="w-100 mb-2">
                    <Form.Check
                      type="checkbox"
                      id="modo-automatico"
                      label="Modo automático (balanceamento por carteira ativa)"
                      checked={values.modoAutomatico}
                      onChange={(e) =>
                        setFieldValue('modoAutomatico', e.target.checked)
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      id="ch11"
                      label="Incluir cheques devolvidos alínea 11"
                      checked={values.incluirChequeDevolvidoAlinea11}
                      onChange={(e) =>
                        setFieldValue(
                          'incluirChequeDevolvidoAlinea11',
                          e.target.checked
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-2">
                <Col className="d-flex flex-wrap" style={{ gap: 8 }}>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading || loadingLookups}
                  >
                    {loading ? 'Pesquisando...' : 'Pesquisar boletos'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      resetForm();
                      if (onLimpar) onLimpar();
                    }}
                  >
                    Limpar filtros
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default SecaoFiltros;
